import { useRef, type KeyboardEvent, type MouseEvent, type PointerEvent } from "react";
import { BookOpen } from "lucide-react";

export function ShelfBookListItem({
  title,
  author,
  coverUrl,
  lastReadLabel,
  latestChapterLabel,
  unreadCount,
  onOpen,
  onOpenMenu,
}: {
  title: string;
  author?: string;
  coverUrl?: string;
  lastReadLabel: string;
  latestChapterLabel: string;
  unreadCount?: number | null;
  onOpen: () => void;
  onOpenMenu?: () => void;
}) {
  const interaction = useBookCardInteraction({ onOpen, onOpenMenu });

  return (
    <div className="py-1">
      <div
        tabIndex={0}
        onClick={interaction.handleClick}
        onKeyDown={interaction.handleKeyDown}
        onContextMenu={interaction.handleContextMenu}
        onPointerDown={interaction.handlePointerDown}
        onPointerUp={interaction.handlePointerUp}
        onPointerCancel={interaction.handlePointerCancel}
        onPointerLeave={interaction.handlePointerCancel}
        className="relative flex cursor-pointer items-center gap-4 rounded-md border px-3 py-2 transition-colors hover:bg-accent/30 focus-visible:bg-accent/30 focus-visible:outline-none"
      >
        {Boolean(unreadCount) && (
          <span className="absolute top-3 right-3 rounded-full bg-foreground px-2 py-1 text-[11px] leading-none text-background">
            {unreadCount}
          </span>
        )}
        <BookCover title={title} coverUrl={coverUrl} className="h-24 w-16 shrink-0" />
        <div className="min-w-0 flex-1 pr-10">
          <h3 className="truncate font-semibold">{title}</h3>
          <p className="mt-1 text-xs text-muted-foreground">{author ?? "未知作者"}</p>
          <div className="mt-3 space-y-1 text-xs text-muted-foreground">
            <p>上次阅读 {lastReadLabel}</p>
            <p>最新章节 {latestChapterLabel}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ShelfBookGridItem({
  title,
  coverUrl,
  onOpen,
  onOpenMenu,
}: {
  title: string;
  coverUrl?: string;
  onOpen: () => void;
  onOpenMenu?: () => void;
}) {
  const interaction = useBookCardInteraction({ onOpen, onOpenMenu });

  return (
    <div
      tabIndex={0}
      onClick={interaction.handleClick}
      onKeyDown={interaction.handleKeyDown}
      onContextMenu={interaction.handleContextMenu}
      onPointerDown={interaction.handlePointerDown}
      onPointerUp={interaction.handlePointerUp}
      onPointerCancel={interaction.handlePointerCancel}
      onPointerLeave={interaction.handlePointerCancel}
      className="cursor-pointer space-y-2 rounded-md p-2 transition-colors hover:bg-accent/30 focus-visible:bg-accent/30 focus-visible:outline-none"
    >
      <BookCover title={title} coverUrl={coverUrl} className="aspect-[3/4] w-full" />
      <p className="line-clamp-2 text-sm font-medium">{title}</p>
    </div>
  );
}

function BookCover({ title, coverUrl, className }: { title: string; coverUrl?: string; className: string }) {
  return (
    <div className={`${className} overflow-hidden border bg-muted text-muted-foreground`}>
      {coverUrl ? (
        <img src={coverUrl} alt={title} className="h-full w-full object-cover" loading="lazy" />
      ) : (
        <span className="flex h-full w-full items-center justify-center">
          <BookOpen className="size-6" />
        </span>
      )}
    </div>
  );
}

function useBookCardInteraction({
  onOpen,
  onOpenMenu,
}: {
  onOpen: () => void;
  onOpenMenu?: () => void;
}) {
  const longPressTimerRef = useRef<number | null>(null);
  // 长按打开菜单后 pointerup 仍可能触发 click 需要吞掉下一次点击
  const suppressNextClickRef = useRef(false);

  function clearLongPressTimer() {
    if (longPressTimerRef.current === null) return;
    window.clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = null;
  }

  function handleClick() {
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      return;
    }

    onOpen();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "ContextMenu" && onOpenMenu) {
      event.preventDefault();
      onOpenMenu();
      return;
    }

    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onOpen();
  }

  function handleContextMenu(event: MouseEvent<HTMLDivElement>) {
    if (!onOpenMenu) return;
    event.preventDefault();
    event.stopPropagation();
    onOpenMenu();
  }

  function handlePointerDown(event: PointerEvent<HTMLDivElement>) {
    if (!onOpenMenu || event.pointerType === "mouse") return;

    // 触屏设备通过长按打开详情菜单
    clearLongPressTimer();
    longPressTimerRef.current = window.setTimeout(() => {
      suppressNextClickRef.current = true;
      onOpenMenu();
      clearLongPressTimer();
    }, 420);
  }

  function handlePointerUp() {
    clearLongPressTimer();
  }

  function handlePointerCancel() {
    clearLongPressTimer();
  }

  return {
    handleClick,
    handleKeyDown,
    handleContextMenu,
    handlePointerDown,
    handlePointerUp,
    handlePointerCancel,
  };
}
